import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { LeadsService } from "../../../services/user/leads.services";
import DataTable from "react-data-table-component";
import { ActivityService } from "../../../services/user/activity.services";
import { QuoteService } from "../../../services/user/quotes.services";
import {
    handleNotify,
    TOASTER_POSITION,
    TOASTER_TYPE,
} from "../../common/notification/toaster_notify.component";
import { InvoiceService } from "../../../services/user/invoices.services";
import {
    handleCopy,
    handleExcel,
    handlePDF,
    handlePrint,
} from "../../../utils/filedownload";

const DashboardDetailsComponent = () => {
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [leadSearchText, setLeadSearchText] = useState("");
    const [activitySearchText, setActivitySearchText] = useState("");
    const [quoteSearchText, setQuoteSearchText] = useState("");
    const [invoiceSearchText, setInvoiceSearchText] = useState("");
    const [data, setData] = useState([]);
    const [activityData, setActivityData] = useState([]);
    const [quoteData, setQuoteData] = useState([]);
    const [invoiceData, setInvoiceData] = useState([]);
    const [loading, setLoading] = useState(true);

    // ====================== FETCH DATA ======================
    useEffect(() => {
        const fetchLeadData = async () => {
            try {
                setLoading(true);
                const response = await LeadsService.getAllLeads(selectedStore);
                setData(response);
            } catch (error) {
                console.error("Error fetching leads:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeadData();
    }, [selectedStore]);

    useEffect(() => {
        const fetchActivityData = async () => {
            try {
                setLoading(true);
                const response = await ActivityService.getAllActivities(selectedStore);
                setActivityData(response);
            } catch (error) {
                console.error("Error fetching activities:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchActivityData();
    }, [selectedStore]);

    useEffect(() => {
        const fetchQuoteData = async () => {
            try {
                setLoading(true);
                const response = await QuoteService.getAllQuotes(selectedStore);
                setQuoteData(response);
            } catch (error) {
                handleNotify(
                    "Error fetching quotes",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
                setQuoteData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchQuoteData();
    }, [selectedStore]);

    useEffect(() => {
        const fetchInvoiceData = async () => {
            try {
                setLoading(true);
                const response = await InvoiceService.getAllInvoices(selectedStore);
                setInvoiceData(response);
            } catch (error) {
                handleNotify(
                    "Error fetching invoices",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
                setInvoiceData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoiceData();
    }, [selectedStore]);

    // ====================== LEAD TABLE ======================
    const leadsByUserAndStatus = data.reduce((acc, lead) => {
        const user = lead.user?.username || "Unknown";
        //  const status = lead.status?.statusName || "Unknown";
        const statusRaw = lead.status?.statusName || "Unknown";
        const status = statusRaw.replace(/\s/g, "");
        const leadValue = lead.leadValue || 0;


        if (!acc[user]) {
            acc[user] = {
                New: { count: 0, value: 0 },
                Contacted: { count: 0, value: 0 },
                Quoted: { count: 0, value: 0 },
                Demo: { count: 0, value: 0 },
                Won: { count: 0, value: 0 },
                Lost: { count: 0, value: 0 },
                Junk: { count: 0, value: 0 },
                NoRequirement: { count: 0, value: 0 },
            };
        }

        if (acc[user][status]) {
            acc[user][status].count += 1;
            acc[user][status].value += leadValue;
        }

        return acc;
    }, {});

    const leadTableData = Object.keys(leadsByUserAndStatus).map((user) => {
        const s = leadsByUserAndStatus[user];
        return {
            user,
            New: `${s.New.count} ( ₹ ${s.New.value.toFixed(2)} )`,
            Contacted: `${s.Contacted.count} ( ₹ ${s.Contacted.value.toFixed(2)} )`,
            Quoted: `${s.Quoted.count} ( ₹ ${s.Quoted.value.toFixed(2)} )`,
            Demo: `${s.Demo.count} ( ₹ ${s.Demo.value.toFixed(2)} )`,
            Won: `${s.Won.count} ( ₹ ${s.Won.value.toFixed(2)} )`,
            Lost: `${s.Lost.count} ( ₹ ${s.Lost.value.toFixed(2)} )`,
            Junk: `${s.Junk.count} ( ₹ ${s.Junk.value.toFixed(2)} )`,
            NoRequirement: `${s.NoRequirement.count} ( ₹ ${s.NoRequirement.value.toFixed(
                2
            )} )`,
        };
    });

    // ====================== FILTER HELPERS ======================
    const filterLeadData = leadTableData.filter((row) =>
        Object.values(row).some((val) =>
            String(val).toLowerCase().includes(leadSearchText.toLowerCase())
        )
    );

    const leadColumns = [
        { name: "User Name", selector: (row) => row.user, sortable: true },
        { name: "New", selector: (row) => row.New, sortable: true },
        { name: "Contacted", selector: (row) => row.Contacted, sortable: true },
        { name: "Quoted", selector: (row) => row.Quoted, sortable: true },
        { name: "Demo", selector: (row) => row.Demo, sortable: true },
        { name: "Won", selector: (row) => row.Won, sortable: true },
        { name: "Lost", selector: (row) => row.Lost, sortable: true },
        { name: "Junk", selector: (row) => row.Junk, sortable: true },
        { name: "No Requirement", selector: (row) => row.NoRequirement, sortable: true },
    ];

    // ====================== ACTIVITY TABLE ======================
    const activityByUserAndStatus = activityData.reduce((acc, activity) => {
        const user = activity.user?.username || "Unknown";
        const statusRaw = activity.status?.statusName || "Unknown";
        const status = statusRaw.replace(/\s/g, "");

        if (!acc[user]) {
            acc[user] = {
                NotStarted: { count: 0 },
                InProgress: { count: 0 },
                Completed: { count: 0 },
                Cancelled: { count: 0 },
                Lost: { count: 0 },
                NoRequirement: { count: 0 },
            };
        }

        if (acc[user][status]) {
            acc[user][status].count += 1;
        }

        return acc;
    }, {});

    const activityTableData = Object.keys(activityByUserAndStatus).map((user) => {
        const s = activityByUserAndStatus[user];
        return {
            user,
            NotStarted: s.NotStarted.count,
            InProgress: s.InProgress.count,
            Completed: s.Completed.count,
            Cancelled: s.Cancelled.count,
            Lost: s.Lost.count,
            NoRequirement: s.NoRequirement.count,
        };
    });

    // ====================== FILTER HELPERS ======================
    const filterActivityData = activityTableData.filter((row) =>
        Object.values(row).some((val) =>
            String(val).toLowerCase().includes(activitySearchText.toLowerCase())
        )
    );

    const activityColumns = [
        { name: "User Name", selector: (row) => row.user, sortable: true },
        { name: "Not Started", selector: (row) => row.NotStarted, center: true },
        { name: "In Progress", selector: (row) => row.InProgress, center: true },
        { name: "Completed", selector: (row) => row.Completed, center: true },
        { name: "Cancelled", selector: (row) => row.Cancelled, center: true },
        { name: "Lost", selector: (row) => row.Lost, center: true },
        { name: "No Requirement", selector: (row) => row.NoRequirement, center: true },
    ];

    // ====================== QUOTES TABLE ======================
    const quotesByUserAndStatus = quoteData.reduce((acc, quote) => {
        const user = quote.user?.username || "Unknown";
        // const status = quote.status?.statusName || "Unknown";
        const statusRaw = quote.status?.statusName || "Unknown";
        const status = statusRaw.replace(/\s/g, "");
        const quoteValue = Number(quote.total || 0);

        if (!acc[user]) {
            acc[user] = {
                Draft: { count: 0, value: 0 },
                Delivered: { count: 0, value: 0 },
                Negotiation: { count: 0, value: 0 },
                Won: { count: 0, value: 0 },
                Lost: { count: 0, value: 0 },
                Approved: { count: 0 },
                Unapproved: { count: 0 },
            };
        }

        if (acc[user][status]) {
            acc[user][status].count += 1;
            if (status !== "Approved" && status !== "Unapproved") {
                acc[user][status].value += quoteValue;
            }
        }
        if (quote.user?.approvalForQuote) {
            acc[user].Approved.count += 1;
        } else {
            acc[user].Unapproved.count += 1;
        }

        return acc;
    }, {});

    const quotesTableData = Object.keys(quotesByUserAndStatus).map((user) => {
        const s = quotesByUserAndStatus[user];
        return {
            user,
            Draft: `${s.Draft.count} ( ₹ ${s.Draft.value.toFixed(2)} )`,
            Delivered: `${s.Delivered.count} ( ₹ ${s.Delivered.value.toFixed(2)} )`,
            Negotiation: `${s.Negotiation.count} ( ₹ ${s.Negotiation.value.toFixed(2)} )`,
            Won: `${s.Won.count} ( ₹ ${s.Won.value.toFixed(2)} )`,
            Lost: `${s.Lost.count} ( ₹ ${s.Lost.value.toFixed(2)} )`,
            Approved: `${s.Approved.count}`,
            Unapproved: `${s.Unapproved.count}`,
        };
    });

    // ====================== FILTER HELPERS ======================
    const filterQuoteData = quotesTableData.filter((row) =>
        Object.values(row).some((val) =>
            String(val).toLowerCase().includes(quoteSearchText.toLowerCase())
        )
    );

    const quoteColumns = [
        { name: "User Name", selector: (row) => row.user, sortable: true },
        { name: "Draft", selector: (row) => row.Draft, sortable: true },
        { name: "Delivered", selector: (row) => row.Delivered, sortable: true },
        { name: "Negotiation", selector: (row) => row.Negotiation, sortable: true },
        { name: "Won", selector: (row) => row.Won, sortable: true },
        { name: "Lost", selector: (row) => row.Lost, sortable: true },
        { name: "Approved", selector: (row) => row.Approved, sortable: true, center: true },
        { name: "Unapproved", selector: (row) => row.Unapproved, sortable: true, center: true },
    ];

    // ====================== INVOICE TABLE ======================
    const invoicesByUserAndStatus = invoiceData.reduce((acc, invoice) => {
        const user = invoice.user?.username || "Unknown";
        // const status = invoice.status?.statusName || "Unknown";
        const statusRaw = invoice.status?.statusName || "Unknown";
        const status = statusRaw.replace(/\s/g, "");
        const invoiceValue = Number(invoice.total || 0);

        if (!acc[user]) {
            acc[user] = {
                Draft: { count: 0, value: 0 },
                Delivered: { count: 0, value: 0 },
                NotPaid: { count: 0, value: 0 },
                PartiallyPaid: { count: 0, value: 0 },
                FullyPaid: { count: 0, value: 0 },
                Approved: { count: 0 },
                Unapproved: { count: 0 },
            };
        }

        if (acc[user][status]) {
            acc[user][status].count += 1;
            if (status !== "Approved" && status !== "Unapproved") {
                acc[user][status].value += invoiceValue;
            }
        }

        if (invoice.user?.approvalForInvoice) {
            acc[user].Approved.count += 1;
        } else {
            acc[user].Unapproved.count += 1;
        }

        return acc;
    }, {});

    const invoiceTableData = Object.keys(invoicesByUserAndStatus).map((user) => {
        const s = invoicesByUserAndStatus[user];
        return {
            user,
            Draft: `${s.Draft.count} ( ₹ ${s.Draft.value.toFixed(2)} )`,
            Delivered: `${s.Delivered.count} ( ₹ ${s.Delivered.value.toFixed(2)} )`,
            NotPaid: `${s.NotPaid.count} ( ₹ ${s.NotPaid.value.toFixed(2)} )`,
            PartiallyPaid: `${s.PartiallyPaid.count} ( ₹ ${s.PartiallyPaid.value.toFixed(2)} )`,
            FullyPaid: `${s.FullyPaid.count} ( ₹ ${s.FullyPaid.value.toFixed(2)} )`,
            Approved: `${s.Approved.count}`,
            Unapproved: `${s.Unapproved.count}`,
        };
    });

    // ====================== FILTER HELPERS ======================
    const filterInvoiceData = invoiceTableData.filter((row) =>
        Object.values(row).some((val) =>
            String(val).toLowerCase().includes(invoiceSearchText.toLowerCase())
        )
    );

    const invoiceColumns = [
        { name: "User Name", selector: (row) => row.user, sortable: true },
        { name: "Draft", selector: (row) => row.Draft, sortable: true },
        { name: "Delivered", selector: (row) => row.Delivered, sortable: true },
        { name: "Not Paid", selector: (row) => row.NotPaid, sortable: true },
        { name: "Partially Paid", selector: (row) => row.PartiallyPaid, sortable: true },
        { name: "Fully Paid", selector: (row) => row.FullyPaid, sortable: true },
        { name: "Approved", selector: (row) => row.Approved, sortable: true, center: true },
        { name: "Unapproved", selector: (row) => row.Unapproved, sortable: true, center: true },
    ];

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                {/* Lead Details Section */}
                <div className="col-12">
                    <h3 className="text-secondary d-flex fs-5">Lead Details</h3>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary mt-3 p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filterLeadData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filterLeadData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filterLeadData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={leadSearchText}
                                    onChange={(e) => setLeadSearchText(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="table-responsive mt-4">
                            <DataTable
                                columns={leadColumns}
                                data={filterLeadData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>
                    </div>
                </div>
                {/* Activity Details Section */}
                <div className="col-12">
                    <h3 className="text-secondary d-flex fs-5">Activity Details</h3>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filterActivityData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filterActivityData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filterActivityData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={activitySearchText}
                                    onChange={(e) => setActivitySearchText(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="table-responsive mt-4">
                            <DataTable
                                columns={activityColumns}
                                data={filterActivityData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>
                    </div>
                </div>
                {/* Quote Details Section */}
                <div className="col-12">
                    <h3 className="text-secondary d-flex fs-5">Quotes Details</h3>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filterQuoteData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filterQuoteData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filterQuoteData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={quoteSearchText}
                                    onChange={(e) => setQuoteSearchText(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="table-responsive mt-4">
                            <DataTable
                                columns={quoteColumns}
                                data={filterQuoteData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>
                    </div>
                </div>
                {/* Invoice Details Section */}
                <div className="col-12">
                    <h3 className="text-secondary d-flex fs-5">Invoice Details</h3>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filterInvoiceData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filterInvoiceData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filterInvoiceData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={invoiceSearchText}
                                    onChange={(e) => setInvoiceSearchText(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="table-responsive mt-4">
                            <DataTable
                                columns={invoiceColumns}
                                data={filterInvoiceData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default DashboardDetailsComponent;

